import React, {
  useEffect,
} from "react";

import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";

import {
  MEDIA_MODE,
} from "hook/useDevices";

import MicLevelBars from "./MicLevelBars";

const VideoPreviewCard = ({
  videoRef,
  mediaMode,
  devices,
}) => {
const {
  camStatus,
  micStatus,

  camDevices,
  activeCamId,

  camOn,
  micOn,
  micLevel,

  streamRef,

  toggleCam,
  toggleMic,
  switchCamera,
} = devices;

  const isAudioOnly =
    mediaMode ===
    MEDIA_MODE.AUDIO_ONLY;

    useEffect(() => {
  if (
    isAudioOnly ||
    camStatus !== "pass" ||
    !camOn ||
    !videoRef?.current ||
    !streamRef?.current
  ) {
    return;
  }

  const videoElement =
    videoRef.current;

  if (
    videoElement.srcObject !==
    streamRef.current
  ) {
    videoElement.srcObject =
      streamRef.current;
  }

  const playPromise =
    videoElement.play?.();

  if (
    playPromise &&
    typeof playPromise.catch ===
      "function"
  ) {
    playPromise.catch(() => {
      /*
       * The element is muted and autoplay is
       * requested, so this normally succeeds.
       *
       * If a browser still blocks playback,
       * do not mark the camera itself as failed.
       */
    });
  }
}, [
  isAudioOnly,
  camStatus,
  camOn,
  videoRef,
  streamRef,
]);

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom:
            "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
          >
            Your Preview
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            Check how you will appear and sound before joining.
          </Typography>
        </Box>

        {isAudioOnly && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
              py: 0.5,
              borderRadius: 1.5,
              bgcolor:
                "rgba(2,136,209,0.08)",
              color:
                "primary.main",
            }}
          >
            <PrivacyTipOutlinedIcon
              fontSize="small"
            />

            <Typography
              variant="caption"
              fontWeight={700}
            >
              Privacy Mode
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          bgcolor: "#101722",
          display: "flex",
          alignItems: "center",
          justifyContent:
            "center",
          overflow: "hidden",
        }}
      >
        {!isAudioOnly &&
        camStatus === "pass" &&
        camOn ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform:
                "scaleX(-1)",
            }}
          />
        ) : isAudioOnly ? (
          <Stack
            spacing={1}
            alignItems="center"
            textAlign="center"
            sx={{
              px: 2,
            }}
          >
            <PrivacyTipOutlinedIcon
              sx={{
                fontSize: 48,
                color:
                  "rgba(255,255,255,0.55)",
              }}
            />

            <Typography
              color="white"
              fontWeight={700}
            >
              Audio Only
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color:
                  "rgba(255,255,255,0.6)",
              }}
            >
              Your camera is not being used.
            </Typography>
          </Stack>
        ) : (
          <Stack
            spacing={1}
            alignItems="center"
            textAlign="center"
            sx={{
              px: 2,
            }}
          >
            <VideocamOffOutlinedIcon
              sx={{
                fontSize: 48,
                color:
                  "rgba(255,255,255,0.45)",
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color:
                  "rgba(255,255,255,0.65)",
              }}
            >
              {camStatus === "checking"
                ? "Checking camera..."
                : camStatus === "fail"
                  ? "Camera unavailable"
                  : "Camera is off"}
            </Typography>
          </Stack>
        )}

        {!isAudioOnly &&
          camStatus === "pass" &&
          camOn && (
            <Box
              sx={{
                position:
                  "absolute",
                left: 12,
                bottom: 12,
                bgcolor:
                  "rgba(0,0,0,0.55)",
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                color="white"
                fontWeight={700}
              >
                YOU
              </Typography>
            </Box>
          )}
      </Box>

      <Box
        sx={{
          p: 2,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            {!isAudioOnly && (
              <Button
                fullWidth
                variant={
                  camOn
                    ? "outlined"
                    : "contained"
                }
                color={
                  camOn
                    ? "primary"
                    : "error"
                }
                startIcon={
                  camOn ? (
                    <VideocamOutlinedIcon />
                  ) : (
                    <VideocamOffOutlinedIcon />
                  )
                }
                onClick={toggleCam}
                disabled={
                  camStatus !==
                  "pass"
                }
              >
                {camOn
                  ? "Camera On"
                  : "Camera Off"}
              </Button>
            )}

            <Button
              fullWidth
              variant={
                micOn
                  ? "outlined"
                  : "contained"
              }
              color={
                micOn
                  ? "primary"
                  : "error"
              }
              startIcon={
                micOn ? (
                  <MicOutlinedIcon />
                ) : (
                  <MicOffOutlinedIcon />
                )
              }
              onClick={toggleMic}
              disabled={
                micStatus !==
                "pass"
              }
            >
              {micOn
                ? "Mic On"
                : "Mic Off"}
            </Button>
          </Stack>

          {micStatus === "pass" && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Microphone level
              </Typography>

              <MicLevelBars
                level={
                  micOn
                    ? micLevel
                    : 0
                }
                active={
                  micOn
                }
              />
            </Box>
          )}

          {!isAudioOnly &&
            camDevices.length > 1 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{
                    mb: 0.75,
                  }}
                >
                  Camera
                </Typography>

                <FormControl
                  fullWidth
                  size="small"
                >
                  <Select
                    value={
                      activeCamId ||
                      ""
                    }
                    onChange={(
                      event,
                    ) => {
                      switchCamera(
                        event.target
                          .value,
                      );
                    }}
                  >
                    {camDevices.map(
                      (
                        camera,
                        index,
                      ) => (
                        <MenuItem
                          key={
                            camera.deviceId
                          }
                          value={
                            camera.deviceId
                          }
                        >
                          {camera.label ||
                            `Camera ${
                              index +
                              1
                            }`}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default VideoPreviewCard;